import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clipboard } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';
import { getBaseUrl } from '@/utils/url';

const INVITE_LINK = getBaseUrl();

// Helper to get the correct Edge Function URL for local/production
const getInviteFunctionUrl = async () => {
  const { getEdgeFunctionUrl } = await import('@/utils/url');
  return getEdgeFunctionUrl('invite_friend');
};

export default function InviteFriend() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const validateEmail = (value: string) => {
    if (!value) return '';
    return /^[^@]+@[^@]+\.[^@]+$/.test(value) ? '' : t('invite_friend.invalid_email');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(INVITE_LINK);
      setCopied(true);
      toast({ title: t('notifications.toasts.link_copied'), description: t('notifications.toasts.link_copied_description'), variant: 'default' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
      toast({ title: t('notifications.toasts.link_copy_failed'), description: t('notifications.toasts.link_copy_failed_description'), variant: 'destructive' });
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailError(validateEmail(e.target.value));
  };

  const handleBack = () => {
    if (user) {
      // Se logado, vai para home
      navigate('/home');
    } else {
      // Se não logado, vai para sign in/signup
      navigate('/');
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(validateEmail(email));
    if (validateEmail(email)) return;
    setLoading(true);
    
    try {
      console.log('📧 Sending invite to:', email);
      
      // Call the Edge Function via supabase client to ensure authentication
      const { data, error } = await supabase.functions.invoke('invite_friend', {
        body: { email },
      });
      
      console.log('📬 Response data:', data);
      console.log('❌ Response error:', error);
      
      if (error) {
        console.error('❌ Full error object:', error);
        const backendMsg =
          (typeof error.error === 'string' ? error.error : error.error?.message) ||
          error.message ||
          t('invite_friend.failed_to_send');
        toast({ title: backendMsg, variant: 'destructive' });
        setLoading(false);
        return;
      }
      
      toast({ title: t('notifications.toasts.invite_sent'), description: t('notifications.toasts.invite_sent_description', { email }), variant: 'default' });
      setEmail('');
    } catch (err) {
      console.error('❌ Catch error:', err);
      toast({ title: t('notifications.toasts.invite_failed'), description: t('notifications.toasts.invite_failed_description'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TopNavigation />
      <div className="min-h-screen bg-[#160C0C] text-white">
        
        {/* Header */}
        <div className="pt-16">
          <div className="relative flex items-center px-4 py-3.5 border-b border-[#281B1B]">
            <button onClick={handleBack} className="p-1 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold text-white truncate max-w-[60%]">{t('invite_friend.title')}</h1>
          </div>

          {/* Content */}
          <div className="p-4">
            <div className="max-w-md mx-auto">
              <form onSubmit={handleInvite} className="w-full space-y-8">
                
                {/* Copy Link Section */}
                <div className="space-y-2 mb-8">
                  <p className="text-gray-300">{t('invite_friend.share_description')}</p>
                  <div className="flex items-stretch w-full gap-2">
                    <input
                      type="text"
                      value={INVITE_LINK}
                      readOnly
                      className="font-medium flex-1 h-12 w-full box-border rounded-md border border-white bg-white/5 px-3 py-2 text-base text-white placeholder:text-white/60 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <button
                      onClick={handleCopy}
                      type="button"
                      className="p-2 h-12 bg-sossiety-red-600 text-white rounded-lg hover:bg-sossiety-red-700 transition flex items-center justify-center"
                      aria-label="Copy link"
                      style={{ minWidth: 48 }}
                    >
                      <Clipboard className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                {/* Divider */}
                <div className="flex items-center my-6">
                  <div className="flex-grow border-t border-[#232326]" />
                  <span className="mx-4 text-gray-400 font-semibold">{t('invite_friend.or_divider')}</span>
                  <div className="flex-grow border-t border-[#232326]" />
                </div>
                
                {/* Invite by Email Section */}
                <div className="space-y-4">
                  <label className="block mb-1 font-medium text-gray-200">{t('invite_friend.email_label')}</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="friend@email.com"
                    error={!!emailError}
                    disabled={loading}
                  />
                  {emailError && <div className="text-red-400 text-sm">{emailError}</div>}
                  <button
                    type="submit"
                    disabled={!!emailError || !email || loading}
                    className="w-full px-4 py-4 bg-sossiety-red-600 text-white rounded-full font-semibold hover:bg-sossiety-red-700 transition disabled:opacity-50 mt-2"
                    style={{ borderRadius: '9999px' }}
                  >
                    {loading ? t('common.sending') : t('invite_friend.invite_button')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <BottomNavigation />
    </>
  );
}